import { builder } from '@atlassian/state-machine/builder';
import { keep } from '@atlassian/state-machine/common/context';
import { always } from '@atlassian/state-machine/common/guards';

type OutsideXMLState =
  | 'initial'
  | 'edit_outside'
  | 'edit_outside_post_meta'
  | 'edit_outside_post_data'
  | 'create_outside'
  | 'create_outside_post_fields'
  | 'create_outside_post_views'
  | 'create_outside_post_data';
type InsideXMLState =
  | 'create_fields'
  | 'create_views'
  | 'create_data'
  | 'edit_data'
  | 'edit_meta';
type State = OutsideXMLState | InsideXMLState;

type Event =
  | OpeningBracketEvent
  | ClosingBracketEvent
  | EnteringFieldsTag
  | EnteringMetaTagEvent;
type OpeningBracketEvent = { type: 'openingBracket' };
type ClosingBracketEvent = { type: 'closingBracket' };
type EnteringFieldsTag = { type: 'enteringFieldsTag' };
type EnteringMetaTagEvent = { type: 'enteringMetaTag' };

type EventLookup = {
  openingBracket: OpeningBracketEvent;
  closingBracket: ClosingBracketEvent;
  enteringFieldsTag: EnteringFieldsTag;
  enteringMetaTag: EnteringMetaTagEvent;
};
type Context = Record<string, unknown>;

export const isAnOutsideState = (state: State): boolean => {
  return (
    state === 'create_outside' ||
    state === 'create_outside_post_fields' ||
    state === 'create_outside_post_views' ||
    state === 'create_outside_post_data' ||
    state === 'edit_outside' ||
    state === 'edit_outside_post_meta' ||
    state === 'edit_outside_post_data'
  );
};

export const createParsingMachine = () =>
  builder<State, Event, EventLookup, Context>()
    .initial('initial')
    .context({})
    .state('initial', (s) =>
      s
        .when('enteringFieldsTag', (t) => t.if(always).context(keep).to('create_fields'))
        .when('enteringMetaTag', (t) => t.if(always).context(keep).to('edit_meta'))
    )
    .state('create_outside', (s) =>
      s.when('openingBracket', (t) => t.if(always).context(keep).to('create_fields'))
    )
    .state('create_fields', (s) =>
      s.when('closingBracket', (t) =>
        t.if(always).context(keep).to('create_outside_post_fields')
      )
    )
    .state('create_outside_post_fields', (s) =>
      s.when('openingBracket', (t) => t.if(always).context(keep).to('create_views'))
    )
    .state('create_views', (s) =>
      s.when('closingBracket', (t) =>
        t.if(always).context(keep).to('create_outside_post_views')
      )
    )
    .state('create_outside_post_views', (s) =>
      s.when('openingBracket', (t) => t.if(always).context(keep).to('create_data'))
    )
    .state('create_data', (s) =>
      s.when('closingBracket', (t) =>
        t.if(always).context(keep).to('create_outside_post_data')
      )
    )
    .state('edit_meta', (s) =>
      s.when('closingBracket', (t) =>
        t.if(always).context(keep).to('edit_outside_post_meta')
      )
    )
    .state('edit_outside_post_meta', (s) =>
      s.when('openingBracket', (t) => t.if(always).context(keep).to('edit_data'))
    )
    .state('edit_data', (s) =>
      s.when('closingBracket', (t) =>
        t.if(always).context(keep).to('edit_outside_post_data')
      )
    )
    .state('create_outside_post_data', (s) => s)
    .state('edit_outside_post_data', (s) => s)
    .build();
